// src/components/GameStartAnnouncementModal.tsx
// 📢 Modal para anunciar el inicio del juego en Google Classroom
// Los alumnos reciben notificación con el código de sala y link para unirse

import { useState, useEffect, useMemo } from "react";
import {
  getCourses,
  createAnnouncement,
  type ClassroomCourse,
} from "../services/classroomService";

interface GameStartAnnouncementModalProps {
  isOpen: boolean;
  onClose: () => void;
  gameId: string;
  roomCode: string;
  gameName: string;
  language: "es" | "en" | "pt";
}

type ModalView = "select-course" | "preview" | "publishing" | "success" | "error";

export function GameStartAnnouncementModal({
  isOpen,
  onClose,
  gameId,
  roomCode,
  gameName,
  language,
}: GameStartAnnouncementModalProps) {
  const [view, setView] = useState<ModalView>("select-course");
  const [courses, setCourses] = useState<ClassroomCourse[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<ClassroomCourse | null>(null);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [error, setError] = useState<string>("");

  const texts = {
    es: {
      title: "📢 Anunciar en Google Classroom",
      selectCourse: "Seleccioná el curso",
      selectCourseHint: "Los alumnos recibirán una notificación para unirse al juego",
      noCourses: "No se encontraron cursos activos",
      noCoursesHint: "Asegurate de ser docente en al menos un curso.",
      loading: "Cargando cursos...",
      preview: "Vista previa del anuncio",
      previewHint: "Este mensaje se publicará en el curso:",
      publish: "📢 Publicar anuncio",
      publishing: "Publicando...",
      cancel: "Cancelar",
      back: "← Volver",
      success: "¡Anuncio publicado!",
      successHint: "Los alumnos recibirán una notificación para unirse.",
      close: "Cerrar",
      error: "Error",
      retry: "Reintentar",
      // Mensaje del anuncio
      announcementTitle: "🎮 ¡Comenzamos el juego!",
      announcementIntro: `Vamos a jugar "${gameName}".`,
      announcementCodeLabel: "Código de sala:",
      announcementLinkLabel: "O entrá directamente:",
      announcementCallToAction: "¡Tu equipo te espera!",
    },
    en: {
      title: "📢 Announce on Google Classroom",
      selectCourse: "Select a course",
      selectCourseHint: "Students will receive a notification to join the game",
      noCourses: "No active courses found",
      noCoursesHint: "Make sure you are a teacher in at least one course.",
      loading: "Loading courses...",
      preview: "Announcement preview",
      previewHint: "This message will be posted to the course:",
      publish: "📢 Publish announcement",
      publishing: "Publishing...",
      cancel: "Cancel",
      back: "← Back",
      success: "Announcement published!",
      successHint: "Students will receive a notification to join.",
      close: "Close",
      error: "Error",
      retry: "Retry",
      // Announcement message
      announcementTitle: "🎮 Game time!",
      announcementIntro: `We're going to play "${gameName}".`,
      announcementCodeLabel: "Room code:",
      announcementLinkLabel: "Or join directly:",
      announcementCallToAction: "Your team is waiting!",
    },
    pt: {
      title: "📢 Anunciar no Google Classroom",
      selectCourse: "Selecione um curso",
      selectCourseHint: "Os alunos receberão uma notificação para entrar no jogo",
      noCourses: "Nenhum curso ativo encontrado",
      noCoursesHint: "Certifique-se de ser professor em pelo menos um curso.",
      loading: "Carregando cursos...",
      preview: "Prévia do anúncio",
      previewHint: "Esta mensagem será publicada no curso:",
      publish: "📢 Publicar anúncio",
      publishing: "Publicando...",
      cancel: "Cancelar",
      back: "← Voltar",
      success: "Anúncio publicado!",
      successHint: "Os alunos receberão uma notificação para entrar.",
      close: "Fechar",
      error: "Erro",
      retry: "Tentar novamente",
      // Mensagem do anúncio
      announcementTitle: "🎮 Hora do jogo!",
      announcementIntro: `Vamos jogar "${gameName}".`,
      announcementCodeLabel: "Código da sala:",
      announcementLinkLabel: "Ou entre diretamente:",
      announcementCallToAction: "Sua equipe está esperando!",
    },
  };

  const t = texts[language];

  // Base URL
  const baseUrl = typeof window !== "undefined" 
    ? window.location.origin 
    : "https://www.thetrafficlightgame.com";

  // ✅ CAMBIO 4: URL con query param en lugar de path param
  const joinLink = `${baseUrl}/join?code=${roomCode}`;

  // Generar texto del anuncio
  const announcementText = useMemo(() => {
    let text = `${t.announcementTitle}\n\n`;
    text += `${t.announcementIntro}\n\n`;
    text += `📋 ${t.announcementCodeLabel} ${roomCode}\n\n`;
    text += `🔗 ${t.announcementLinkLabel}\n${joinLink}\n\n`;
    text += `${t.announcementCallToAction}`;
    return text;
  }, [t, roomCode, joinLink]);

  // Cargar cursos al abrir
  useEffect(() => {
    if (isOpen) {
      loadCourses();
    }
  }, [isOpen]);

  // Reset al cerrar
  useEffect(() => {
    if (!isOpen) {
      setView("select-course");
      setSelectedCourse(null);
      setError("");
    }
  }, [isOpen]);

  const loadCourses = async () => {
    setLoadingCourses(true);
    setError("");
    try {
      const coursesData = await getCourses();
      setCourses(coursesData);
      setView("select-course");
    } catch (err: any) {
      setError(err.message || "Error loading courses");
      setView("error");
    } finally {
      setLoadingCourses(false);
    }
  };

  const handleSelectCourse = (course: ClassroomCourse) => {
    setSelectedCourse(course);
    setView("preview");
  };

  const handlePublish = async () => {
    if (!selectedCourse) return;

    setView("publishing");
    setError("");

    try {
      await createAnnouncement(selectedCourse.id, announcementText);
      setView("success");
    } catch (err: any) {
      setError(err.message || "Error publishing");
      setView("error");
    }
  };

  if (!isOpen) return null;

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        
        {/* ========== SELECCIONAR CURSO ========== */}
        {view === "select-course" && (
          <>
            <div style={headerStyle}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{t.title}</h2>
              <button onClick={onClose} style={closeButtonStyle}>✕</button>
            </div>

            <div style={{ padding: 20 }}>
              <p style={{ margin: "0 0 8px", fontWeight: 600, color: "#334155" }}>
                {t.selectCourse}
              </p>
              <p style={{ margin: "0 0 16px", fontSize: 13, color: "#64748b" }}>
                {t.selectCourseHint}
              </p>

              {loadingCourses ? (
                <div style={{ textAlign: "center", padding: 40 }}>
                  <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
                  <p style={{ color: "#64748b" }}>{t.loading}</p>
                </div>
              ) : courses.length === 0 ? (
                <div style={{
                  textAlign: "center",
                  padding: 32,
                  backgroundColor: "#fef3c7",
                  borderRadius: 12,
                  border: "1px solid #fbbf24",
                }}>
                  <p style={{ margin: "0 0 8px", fontWeight: 600, color: "#92400e" }}>
                    {t.noCourses}
                  </p>
                  <p style={{ margin: 0, fontSize: 13, color: "#a16207" }}>
                    {t.noCoursesHint}
                  </p>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 300, overflowY: "auto" }}>
                  {courses.map((course) => (
                    <button
                      key={course.id}
                      onClick={() => handleSelectCourse(course)}
                      style={{
                        padding: "14px 16px",
                        borderRadius: 10,
                        border: "2px solid #e2e8f0",
                        backgroundColor: "white",
                        cursor: "pointer",
                        textAlign: "left",
                        transition: "all 0.15s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = "#4285f4";
                        e.currentTarget.style.backgroundColor = "#eff6ff";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = "#e2e8f0";
                        e.currentTarget.style.backgroundColor = "white";
                      }}
                    >
                      <div style={{ fontWeight: 600, color: "#1e293b" }}>{course.name}</div>
                      {course.section && (
                        <div style={{ fontSize: 13, color: "#64748b" }}>{course.section}</div>
                      )}
                    </button>
                  ))}
                </div>
              )}

              <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end" }}>
                <button onClick={onClose} style={secondaryButtonStyle}>
                  {t.cancel}
                </button>
              </div>
            </div>
          </>
        )}

        {/* ========== PREVIEW ========== */}
        {view === "preview" && selectedCourse && (
          <>
            <div style={headerStyle}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{t.preview}</h2>
              <button onClick={onClose} style={closeButtonStyle}>✕</button>
            </div>

            <div style={{ padding: 20 }}>
              <p style={{ margin: "0 0 4px", fontSize: 13, color: "#64748b" }}>
                Curso: <strong>{selectedCourse.name}</strong>
              </p>
              
              <p style={{ margin: "16px 0 8px", fontSize: 13, color: "#64748b" }}>
                {t.previewHint}
              </p>

              <div style={{
                padding: 16,
                backgroundColor: "#f8fafc",
                borderRadius: 10,
                border: "1px solid #e2e8f0",
                whiteSpace: "pre-wrap",
                fontSize: 14,
                lineHeight: 1.6,
                color: "#334155",
              }}>
                {announcementText}
              </div>

              <div style={{ display: "flex", gap: 12, marginTop: 20 }}>
                <button
                  onClick={() => setView("select-course")}
                  style={secondaryButtonStyle}
                >
                  {t.back}
                </button>
                <button
                  onClick={handlePublish}
                  style={{ ...primaryButtonStyle, flex: 1 }}
                >
                  {t.publish}
                </button>
              </div>
            </div>
          </>
        )}

        {/* ========== PUBLISHING ========== */}
        {view === "publishing" && (
          <div style={{ padding: 40, textAlign: "center" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>📤</div>
            <p style={{ color: "#64748b" }}>{t.publishing}</p>
          </div>
        )}

        {/* ========== SUCCESS ========== */}
        {view === "success" && (
          <div style={{ padding: 40, textAlign: "center" }}>
            <div style={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              backgroundColor: "#dcfce7",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px",
            }}>
              <span style={{ fontSize: 40 }}>✅</span>
            </div>
            <h3 style={{ margin: "0 0 8px", color: "#16a34a" }}>{t.success}</h3>
            <p style={{ margin: "0 0 24px", color: "#64748b", fontSize: 14 }}>
              {t.successHint}
            </p>
            <button onClick={onClose} style={primaryButtonStyle}>
              {t.close}
            </button>
          </div>
        )}

        {/* ========== ERROR ========== */}
        {view === "error" && (
          <div style={{ padding: 40, textAlign: "center" }}>
            <div style={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              backgroundColor: "#fef2f2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px",
            }}>
              <span style={{ fontSize: 40 }}>❌</span>
            </div>
            <h3 style={{ margin: "0 0 8px", color: "#dc2626" }}>{t.error}</h3>
            <p style={{ margin: "0 0 24px", color: "#64748b", fontSize: 14 }}>
              {error}
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <button onClick={onClose} style={secondaryButtonStyle}>
                {t.cancel}
              </button>
              <button onClick={loadCourses} style={primaryButtonStyle}>
                {t.retry}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// ESTILOS
// ============================================

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  backgroundColor: "rgba(0,0,0,0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 10000,
  padding: 20,
};

const modalStyle: React.CSSProperties = {
  backgroundColor: "white",
  borderRadius: 16,
  width: "100%",
  maxWidth: 480,
  maxHeight: "85vh",
  overflow: "hidden",
  boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
};

const headerStyle: React.CSSProperties = {
  padding: "16px 20px",
  borderBottom: "1px solid #e2e8f0",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  backgroundColor: "#eff6ff",
};

const closeButtonStyle: React.CSSProperties = {
  background: "none",
  border: "none",
  fontSize: 20,
  cursor: "pointer",
  color: "#64748b",
  padding: 4,
};

const primaryButtonStyle: React.CSSProperties = {
  padding: "12px 24px",
  fontSize: 14,
  fontWeight: 600,
  borderRadius: 10,
  border: "none",
  backgroundColor: "#4285f4",
  color: "white",
  cursor: "pointer",
};

const secondaryButtonStyle: React.CSSProperties = {
  padding: "12px 24px",
  fontSize: 14,
  fontWeight: 600,
  borderRadius: 10,
  border: "2px solid #e2e8f0",
  backgroundColor: "white",
  color: "#64748b",
  cursor: "pointer",
};